import React, { ReactNode } from 'react'
import { connect, DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { autobind } from 'core-decorators'
import { ISessionState, IStoreState, IWorkersState } from '../../store/states'
import { ITableProperty, CrudPage, ICrudPageState, IWorker, IRole, emptyPage } from '../../models'
import { Menu } from '../../containers'
import {
  Button,
  Panel,
  Table,
  Pagination,
  TableActions,
  DraftWorkerModal,
  PageHeader,
  Input,
  DeleteConfirmationModal
} from '../../components'
import { WorkerService, RoleService } from '../../services'
import { setWorkersAction } from '../../store/actions'

interface IProps {
  session: ISessionState
  page: IWorkersState
}

interface IState extends ICrudPageState<IWorker> {
  roles: IRole[]
}

type Props = IProps & DispatchProp<AnyAction>

// tslint:disable:jsx-no-lambda

class WorkersPage extends CrudPage<IWorker, Props, IState> {
  private tableProperties: ITableProperty[] = [
    { key: 'username', label: 'phoneNumber' },
    { key: 'fullName', label: 'fullName' },
    { key: 'operation', label: 'operation', className: 'no-flex' }
  ]

  private get tableItems(): Array<{}> {
    const items = this.props.page.items
    return items && items.map(x => ({
      username: x.username,
      fullName: x.fullName,
      operation:
        <TableActions onDelete={ () => this.handleOpenDelete(x) } onEdit={ () => this.handleOpenEdit(x) } />
    }))
  }

  constructor(props) {
    super(props)
    this.state = {
      ...this.initialState,
      roles: []
    }
  }

  public componentWillUnmount(): void {
    super.componentWillUnmount()
    this.props.dispatch(setWorkersAction(emptyPage))
  }

  public async componentDidMount(): Promise<void> {
    super.componentDidMount()
    await this.fetchRoles()
  }

  public render(): ReactNode {
    const {
      isCreating,
      isEditing,
      isSendingRequest,
      roles,
      editingItem,
      filters,
      deletingItem
    } = this.state
    const { page, session } = this.props
    return (
      <section className="page fully-aligned work-steps">
        <Menu />
        <article className="content">
          <PageHeader session={ session } label="workers" />
          <div className="filters">
            <Button label="new" className="new-button" onClick={ this.handleOpenCreate } />
            <Input
              value={ filters.query.value }
              placeholder="search"
              isInvalid={ !!filters.query.value && !filters.query.isValid }
              onChange={ x => this.handleFilterChange({ query: { value: x, isValid: x.length >= 3 } }) }
            />
          </div>
          <Panel>
            <Table properties={ this.tableProperties } items={ this.tableItems } />
          </Panel>
          <footer>
            <Pagination
              onClick={ this.fetchItems }
              isHidden={ !page.pagination }
              pagination={ page.pagination }
            />
          </footer>
        </article>
        <DraftWorkerModal
            title={ isCreating ? 'create' : 'save' }
            item={ editingItem }
            isHidden={ !isCreating && !isEditing }
            onSubmit={ (isCreating && this.handleCreate) || (isEditing && this.handleEdit) }
            onCancel={ (isCreating && this.handleCancelCreate) || (isEditing && this.handleCancelEdit) }
            isLoading={ isSendingRequest }
            roles={ roles }
          />
        <DeleteConfirmationModal
          isHidden={ !deletingItem }
          onCancel={ () => this.handleOpenDelete(null) }
          onConfirm={ this.handleDelete }
          isLoading={ isSendingRequest }
        />
      </section>
    )
  }

  @autobind
  protected async handleCreate(worker: IWorker): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      await WorkerService.createWorker(worker)
      await this.fetchItems()
      this.setState({ isCreating: false })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }

  @autobind
  protected async handleEdit(worker: IWorker): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      if (!!worker.newPassword && !!worker.confirmNewPassword
        && worker.newPassword === worker.confirmNewPassword) {
          await WorkerService.updateWorkerPassword(worker)
      }
      await WorkerService.updateWorker(worker)
      await this.fetchItems()
      this.setState({ isEditing: false, editingItem: null })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }

  @autobind
  protected async handleDelete(): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      await WorkerService.deleteWorker(this.state.deletingItem.id)
      this.setState({ deletingItem: null })
      await this.fetchItems()
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }

  @autobind
  protected async fetchItems(page = 1, areArchivedIncluded = false): Promise<void> {
    try {
      const query = this.state.filters.query
      const result = await WorkerService.getWorkers(page, areArchivedIncluded, query)
      this.props.dispatch(setWorkersAction(result))
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    }
  }

  private async fetchRoles(): Promise<void> {
    try {
      const roles = await RoleService.getRoles()
      this.setState({ roles })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }
  }
}

const mapStateToProps = ({ session, workers }: IStoreState): IProps => ({
  session,
  page: workers
})

export default connect(mapStateToProps)(WorkersPage)
